import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  OutlineButton,
  Input,
  Select,
  TextArea,
  FormGroup,
  Label,
  FlexContainer,
  Grid,
  ErrorText
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { FundRequest, FundRequestItem, BudgetCategory } from '../../types';
import apiService from '../../services/api';
import NotificationService from '../../services/NotificationService';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: var(--secondary-color);
  border-bottom: 2px solid var(--light-gray);
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
`;

const ItemTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
  
  th, td {
    padding: 0.75rem;
    text-align: left;
    border-bottom: 1px solid var(--light-gray);
  }
  
  th {
    background-color: var(--light-gray);
    color: var(--secondary-color);
    font-weight: 600;
  }
`;

const ItemFormRow = styled.tr`
  background-color: var(--light-gray);
  
  td {
    padding: 0.5rem;
  }
`;

const TotalRow = styled.tr`
  font-weight: bold;
  
  td {
    padding-top: 1rem;
  }
`;

const ActionButton = styled(Button)`
  padding: 0.25rem 0.5rem;
  font-size: 0.85rem;
`;

const DeleteButton = styled(ActionButton)`
  background-color: var(--danger-color);
  
  &:hover {
    background-color: #c82333;
  }
`;

const FundRequestForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditMode = !!id;
  
  const [formData, setFormData] = useState<{
    projectId: string;
    timeline: string;
    justification: string;
    items: FundRequestItem[];
    newItem: {
      description: string;
      quantity: number;
      unitPrice: number;
      category: BudgetCategory;
      specifications: string;
    };
  }>({
    projectId: '',
    timeline: '',
    justification: '',
    items: [],
    newItem: {
      description: '',
      quantity: 1,
      unitPrice: 0,
      category: 'Materials',
      specifications: ''
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [errors, setErrors] = useState<{
    projectId?: string;
    timeline?: string;
    items?: string;
    newItem?: {
      description?: string;
      quantity?: string;
      unitPrice?: string;
    };
  }>({});
  
  const [projects, setProjects] = useState<any[]>([]);
  
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load projects for dropdown
        const projectsResponse = await apiService.getProjects();
        let availableProjects = projectsResponse.data;
        
        // For supervisors, only show projects they're assigned to
        if (user?.role === 'Supervisor') {
          availableProjects = availableProjects.filter((project: any) => 
            project.assignedStaff?.some((staff: any) => 
              staff.userId === user.id && staff.role === 'Supervisor'
            )
          );
        }
        
        setProjects(availableProjects);
        
        if (isEditMode && id) {
          // Load fund request data for editing
          const fundRequestResponse = await apiService.getFundRequest(id);
          const fundRequest = fundRequestResponse.data;
          
          if (fundRequest) {
            setFormData({
              projectId: fundRequest.projectId,
              timeline: fundRequest.timeline,
              justification: fundRequest.justification || '',
              items: [...fundRequest.items],
              newItem: {
                description: '',
                quantity: 1,
                unitPrice: 0,
                category: 'Materials',
                specifications: ''
              }
            });
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [id, isEditMode, user]);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is edited
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  
  const handleNewItemChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      newItem: {
        ...prev.newItem,
        [name]: name === 'quantity' || name === 'unitPrice' ? parseFloat(value) || 0 : value
      }
    }));
    
    // Clear error when field is edited
    if (errors.newItem && errors.newItem[name as keyof typeof errors.newItem]) {
      setErrors(prev => ({
        ...prev,
        newItem: {
          ...prev.newItem,
          [name]: undefined
        }
      }));
    }
  };
  
  const addItem = () => {
    // Validate new item
    const newItemErrors: {
      description?: string;
      quantity?: string;
      unitPrice?: string;
    } = {};
    
    if (!formData.newItem.description) {
      newItemErrors.description = 'Description is required';
    }
    
    if (formData.newItem.quantity <= 0) {
      newItemErrors.quantity = 'Quantity must be greater than zero';
    }
    
    if (formData.newItem.unitPrice <= 0) {
      newItemErrors.unitPrice = 'Unit price must be greater than zero';
    }
    
    if (Object.keys(newItemErrors).length > 0) {
      setErrors(prev => ({
        ...prev,
        newItem: newItemErrors
      }));
      return;
    }
    
    // Add new item
    const newItem: FundRequestItem = {
      id: `item-${Date.now()}`,
      description: formData.newItem.description,
      quantity: formData.newItem.quantity,
      unitPrice: formData.newItem.unitPrice,
      totalPrice: formData.newItem.quantity * formData.newItem.unitPrice,
      category: formData.newItem.category,
      specifications: formData.newItem.specifications || undefined
    };
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem],
      newItem: {
        description: '',
        quantity: 1,
        unitPrice: 0,
        category: 'Materials',
        specifications: ''
      }
    }));
    
    // Clear items error if it exists
    if (errors.items) {
      setErrors(prev => ({
        ...prev,
        items: undefined
      }));
    }
  };
  
  const removeItem = (id: string) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== id)
    }));
  };
  
  const calculateTotal = () => {
    return formData.items.reduce((total, item) => total + item.totalPrice, 0);
  };
  
  const validateForm = () => {
    const newErrors: typeof errors = {};
    
    if (!formData.projectId) {
      newErrors.projectId = 'Project is required';
    }
    
    if (!formData.timeline) {
      newErrors.timeline = 'Timeline is required';
    }
    
    if (formData.items.length === 0) {
      newErrors.items = 'At least one item is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent, isDraft: boolean = false) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    setSubmitError(null);
    setSubmitSuccess(null);
    
    // Determine approval chain based on user role
    let approvalChain: any[] = [];
    let status = isDraft ? 'Draft' : 'Submitted';
    
    if (user?.role === 'MD' || user?.role === 'TD') {
      // Direct MD/TD requests go straight to Accountant
      approvalChain = [
        {
          role: user.role,
          userId: user.id,
          userName: user.name,
          status: 'INITIATED',
          comments: `Direct request initiated by ${user.role}`,
          timestamp: new Date(),
          order: 1
        },
        {
          role: 'Accountant',
          status: 'Pending',
          order: 2
        }
      ];
      status = 'Under Review';
    } else if (user?.role === 'Supervisor') {
      // Supervisor requests go through normal chain
      approvalChain = [
        {
          role: 'Supervisor',
          userId: user.id,
          userName: user.name,
          status: 'INITIATED',
          comments: 'Request initiated by supervisor',
          timestamp: new Date(),
          order: 1
        },
        {
          role: 'Project Manager',
          status: 'Pending',
          order: 2
        },
        {
          role: 'MD',
          status: 'Pending',
          order: 3
        },
        {
          role: 'Accountant',
          status: 'Pending',
          order: 4
        }
      ];
      status = 'Submitted';
    }
    
    const requestData = {
      ...formData,
      status: status,
      amount: calculateTotal(),
      currency: 'TZS',
      approvalChain: approvalChain,
      requesterName: user?.name || 'System User',
      requesterId: user?.id || 'system',
      requesterRole: user?.role || 'System'
    };
    
    console.log('Submitting request:', requestData);
    
    try {
      if (isEditMode && id) {
        // Update existing fund request
        await apiService.updateFundRequest(id, requestData);
        setSubmitSuccess(`Fund request has been updated successfully.`);
      } else {
        // Create new fund request
        const response = await apiService.createFundRequest(requestData);
        const requestId = response.data._id;
        
        setSubmitSuccess(`Fund request has been created successfully.`);
        
        // Send notifications based on request type
        if (!isDraft) {
          const project = projects.find((p: any) => p._id || p.id === formData.projectId);
          if (project) {
            if (user?.role === 'Supervisor') {
              NotificationService.notifyFundRequestSubmitted(
                requestId,
                project.name,
                requestData.amount,
                user.role
              );
            }
          }
        }
      }
      
      // Navigate after a short delay to show success message
      setTimeout(() => {
        navigate('/funds');
      }, 1500);
    } catch (error: any) {
      console.error('Error submitting fund request:', error);
      setSubmitError(error.message || 'Failed to submit fund request. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  return (
    <div>
      <PageHeader>
        <h1 className="page-title">{isEditMode ? 'Edit Fund Request' : 'New Fund Request'}</h1>
      </PageHeader>
      
      <form onSubmit={(e) => handleSubmit(e, false)}>
        <Card>
          <CardHeader>
            <h3>Request Details</h3>
          </CardHeader>
          <CardBody>
            <FormSection>
              <Grid columns="1fr 1fr" gap="1rem">
                <FormGroup>
                  <Label>Project *</Label>
                  <Select
                    name="projectId"
                    value={formData.projectId}
                    onChange={handleInputChange}
                    className={errors.projectId ? 'error' : ''}
                  >
                    <option value="">Select a project</option>
                    {projects.map((project: any) => (
                      <option key={project._id || project.id} value={project._id || project.id}>
                        {project.name}
                      </option>
                    ))}
                  </Select>
                  {errors.projectId && <ErrorText>{errors.projectId}</ErrorText>}
                </FormGroup>
                
                <FormGroup>
                  <Label>Timeline *</Label>
                  <Input
                    type="text"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleInputChange}
                    placeholder="e.g., 2 weeks"
                    className={errors.timeline ? 'error' : ''}
                  />
                  {errors.timeline && <ErrorText>{errors.timeline}</ErrorText>}
                </FormGroup>
              </Grid>
              
              <Grid columns="1fr 1fr" gap="1rem">
                <FormGroup>
                  <Label>Justification</Label>
                  <TextArea
                    name="justification"
                    value={formData.justification}
                    onChange={handleInputChange}
                    placeholder="Explain why this request is necessary"
                  />
                </FormGroup>
              </Grid>
            </FormSection>
          </CardBody>
        </Card>
        
        <FormSection>
          <Card>
            <CardHeader>
              <h3>Requested Items</h3>
            </CardHeader>
            <CardBody>
              <ItemTable>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Category</th>
                    <th>Quantity</th>
                    <th>Unit Price (TZS)</th>
                    <th>Total (TZS)</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        {item.description}
                        {item.specifications && (
                          <div style={{ fontSize: '0.85rem', color: 'var(--dark-gray)' }}>
                            {item.specifications}
                          </div>
                        )}
                      </td>
                      <td>{item.category}</td>
                      <td>{item.quantity}</td>
                      <td>{item.unitPrice.toLocaleString()}</td>
                      <td>{item.totalPrice.toLocaleString()}</td>
                      <td>
                        <DeleteButton onClick={() => removeItem(item.id)}>
                          Remove
                        </DeleteButton>
                      </td>
                    </tr>
                  ))}
                  
                  {formData.items.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', padding: '1rem' }}>
                        No items added yet. Add an item below.
                      </td>
                    </tr>
                  )}
                  
                  <ItemFormRow>
                    <td>
                      <Input
                        type="text"
                        name="description"
                        value={formData.newItem.description}
                        onChange={handleNewItemChange}
                        placeholder="Item description"
                        className={errors.newItem?.description ? 'error' : ''}
                      />
                      {errors.newItem?.description && (
                        <ErrorText>{errors.newItem.description}</ErrorText>
                      )}
                      <Input
                        type="text"
                        name="specifications"
                        value={formData.newItem.specifications}
                        onChange={handleNewItemChange}
                        placeholder="Specifications (optional)"
                        style={{ marginTop: '0.5rem' }}
                      />
                    </td>
                    <td>
                      <Select
                        name="category"
                        value={formData.newItem.category}
                        onChange={handleNewItemChange}
                      >
                        <option value="Materials">Materials</option>
                        <option value="Labor">Labor</option>
                        <option value="Equipment">Equipment</option>
                        <option value="Sub-Contracts">Sub-Contracts</option>
                        <option value="Others">Others</option>
                      </Select>
                    </td>
                    <td>
                      <Input
                        type="number"
                        name="quantity"
                        value={formData.newItem.quantity}
                        onChange={handleNewItemChange}
                        min="1"
                        className={errors.newItem?.quantity ? 'error' : ''}
                      />
                      {errors.newItem?.quantity && (
                        <ErrorText>{errors.newItem.quantity}</ErrorText>
                      )}
                    </td>
                    <td>
                      <Input
                        type="number"
                        name="unitPrice"
                        value={formData.newItem.unitPrice}
                        onChange={handleNewItemChange}
                        min="0"
                        className={errors.newItem?.unitPrice ? 'error' : ''}
                      />
                      {errors.newItem?.unitPrice && (
                        <ErrorText>{errors.newItem.unitPrice}</ErrorText>
                      )}
                    </td>
                    <td>
                      {(formData.newItem.quantity * formData.newItem.unitPrice).toLocaleString()}
                    </td>
                    <td>
                      <ActionButton type="button" onClick={addItem}>
                        Add Item
                      </ActionButton>
                    </td>
                  </ItemFormRow>
                  
                  <TotalRow>
                    <td colSpan={4} style={{ textAlign: 'right' }}>Total:</td>
                    <td>{calculateTotal().toLocaleString()} TZS</td>
                    <td></td>
                  </TotalRow>
                </tbody>
              </ItemTable>
              
              {errors.items && <ErrorText>{errors.items}</ErrorText>}
            </CardBody>
          </Card>
        </FormSection>
        
        <CardFooter>
          <FlexContainer gap="0.5rem" justify="flex-end">
            <OutlineButton as={Link} to="/funds">
              Cancel
            </OutlineButton>
            <Button type="button" onClick={(e) => handleSubmit(e, true)} disabled={submitLoading}>
              {submitLoading ? 'Saving...' : 'Save as Draft'}
            </Button>
            <Button type="submit" disabled={submitLoading}>
              {submitLoading ? 'Submitting...' : 'Submit Request'}
            </Button>
          </FlexContainer>
        </CardFooter>
      </form>
      
      {submitError && (
        <div style={{ 
          marginTop: '1rem', 
          padding: '0.75rem', 
          backgroundColor: 'var(--danger-color)', 
          color: 'white', 
          borderRadius: 'var(--border-radius)',
          textAlign: 'center'
        }}>
          <strong>Error:</strong> {submitError}
        </div>
      )}

      {submitSuccess && (
        <div style={{ 
          marginTop: '1rem', 
          padding: '0.75rem', 
          backgroundColor: 'var(--success-color)', 
          color: 'white', 
          borderRadius: 'var(--border-radius)',
          textAlign: 'center'
        }}>
          <strong>Success:</strong> {submitSuccess}
        </div>
      )}
      
      <div style={{ textAlign: 'right', padding: '1rem 0', color: 'var(--dark-gray)', fontSize: '0.8rem' }}>
        Developed by Sandfish
      </div>
    </div>
  );
};

export default FundRequestForm; 